import {
  type CanvasElementFrom,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type StyleConfigProps,
  type VisibilityElementProps,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import {
  type CanvasElementPropertiesWithData,
  type ElementLabelPropsWithPositionAndWidth,
  type ElementLabelStyleProps,
  type ElementSizingMode,
  type PropertiesConfig,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
} from '@blaze-shared/elements-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { QrCodeDefinitionMapper } from './ai/qr-code.definition-mapper';
import { QrCodeElementHandler } from './ai/qr-code.element-handler';
import { qrcodeV2Migration } from './migrations/002-qr-code.migration';
import { qrcodeV3PaddingToMarginMigration } from './migrations/003-qr-code-padding-to-margin.migration';
import { DEFAULT_URL, QrCodeComponent } from './qr-code.component';
import { QrCodePropsBuilder } from './qr-code.props-builder';

export { transformToQrCode } from './qr-code.transforms';
export type QrCodeDef = WithProps<QrCodePropsModel> & WithIoConfig;
export type QrCodeElement = CanvasElementFrom<'image'>;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    qrcode: QrCodeDef;
  }
}

const DEFAULT_WIDTH = 170;
const DEFAULT_HEIGHT = 170;
const MIN_WIDTH = 24;
const MIN_HEIGHT = 24;
const MAX_WIDTH = 1920;
const MAX_HEIGHT = 1920;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['fixed', 'auto'];

registerElementDefinition({
  type: 'qrcode',
  componentType: QrCodeComponent,
  isListed: true,
  name: 'QR Code',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: true },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitial(),
  propsBuilder: QrCodePropsBuilder,
  ai: {
    name: 'qrcode',
    handler: () => QrCodeElementHandler,
    definitionMapper: () => QrCodeDefinitionMapper,
  },
  tags: ['Photo'],
  groups: ['advanced'],
  styleIncludePaths: [
    'style',
    'content.title.align',
    'content.title.iconPosition',
    'content.title.position',
    'content.title.width',
  ],
  metaUiFeatures: { type: 'input' },
  version: 3,
  migrations: {
    2: qrcodeV2Migration,
    3: qrcodeV3PaddingToMarginMigration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [{ property: 'value', label: 'URL', dataType: 'String' }],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [],
  };
}

type PropsModel = CanvasElementPropertiesWithData & StyleConfigProps & VisibilityElementProps;

export type QrCodePropsModel = PropsModel & {
  content: {
    url: {
      type: 'dynamic' | 'add' | 'current';
      value: string;
    };
    title: ElementLabelPropsWithPositionAndWidth;
    font: {
      fontSize: ValueWithUnitOfMeasure;
      fontFamily: string;
      lineHeight: ValueWithUnitOfMeasure;
    };
  };
  style: {
    general: {
      title: PropertiesConfigInputColorModel;
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
    };
    labelBlock: ElementLabelStyleProps;
    code: {
      background: PropertiesConfigInputColorModel;
      foreground: PropertiesConfigInputColorModel;
    };
    logo: {
      color: PropertiesConfigInputColorModel;
    };
  };
};

function getDefaults(): PropsOf<'qrcode'> {
  return {
    content: {
      url: {
        type: 'add',
        value: '',
      },
      title: {
        label: 'QR Code',
        position: 'left',
        align: 'left',
        width: '30%',
        enabled: false,
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      font: {
        fontSize: '$t.fontSize',
        fontFamily: '$t.fontFamily',
        lineHeight: '$t.lineHeight',
      },
    },
    style: {
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        offset: { margin: '$t.spacingInput' },
      },
      labelBlock: {
        fontSize: '$t.fontSize',
        fontFamily: '$t.fontFamily',
        fontWeight: '$t.labelFontWeight',
        fontStyle: [],
        iconSize: '16px',
        lineHeight: '$t.lineHeight',
      },
      code: {
        background: { color1: '$t.fillInternal', selectorType: 'solid' },
        foreground: { color1: '$t.title', selectorType: 'solid' },
      },
      logo: {
        color: { color1: '$t.accent', selectorType: 'solid' },
      },
    },
  };
}

function getInitial(): PartialPropsOf<'qrcode'> {
  return {
    content: {
      title: {
        width: '35%',
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('qr-code', [
    epc.section('content', 'Content', [
      epc.subsections.subsection('content.url', 'URL', [
        epc.fields.radiosField('content.url.type', 'Type', [], {
          hideLabel: true,
          options: (props, elementId, resolver) => {
            const isOnForm = elementId
              ? resolver.resolveValue('IS_ON_FORM_VARIANT', { elementId })
              : false;
            const resolve = [
              { value: 'add', label: 'Add URL' },
              { value: 'current', label: 'Current Page' },
            ];
            if (isOnForm) {
              resolve.unshift({ value: 'dynamic', label: 'Dynamic' });
            }
            return resolve;
          },
        }),
        epc.utils.conditionalField(
          epc.fields.textInput('content.url.value', '', {
            placeholder: DEFAULT_URL,
            hideLabel: true,
          }),
          'content.url.type',
          (type: 'dynamic' | 'add' | 'current') => type !== 'add',
        ),
        epc.utils.conditionalField(
          epc.fields.textInfo('content.url.info', 'The QR code will link to the current item'),
          'content.url.type',
          (type: 'dynamic' | 'add' | 'current') => type !== 'dynamic',
        ),
        epc.utils.conditionalField(
          epc.fields.textInfo('content.url.info', 'The QR Code will open this URL'),
          'content.url.type',
          (type: 'dynamic' | 'add' | 'current') => type !== 'add',
        ),
        epc.utils.conditionalField(
          epc.fields.textInfo('content.url.info', 'The QR code will link to the current page'),
          'content.url.type',
          (type: 'dynamic' | 'add' | 'current') => type !== 'current',
        ),
      ]),
      epc.subsections.subsection('content.size', 'Size', [
        epc.root.elementWidthField(
          { min: MIN_WIDTH, max: MAX_WIDTH, unit: 'px', step: 1 },
          WIDTH_OPTIONS,
        ),
        epc.root.elementHeightField(
          { min: MIN_HEIGHT, max: MAX_HEIGHT, unit: 'px', step: 1 },
          HEIGHT_OPTIONS,
        ),
      ]),
      epc.subsections.visibilitySubsection(),
      epc.subsections.labelContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
          }),
        ]),
        epc.subsections.subsection('style.code', 'Code', [
          epc.fields.colorField(`style.code.background`, 'Background', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField(`style.code.foreground`, 'Foreground', {
            allowGradient: false,
            allowNone: false,
          }),
        ]),
        epc.subsections.subsection('style.logo', 'Logo', [
          epc.fields.colorField(`style.logo.color`, 'Color', {
            allowGradient: false,
            allowNone: false,
          }),
        ]),

        epc.subsections.labelStyleSubsection(
          {
            color: 'style.general.title',
            enabled: 'content.title.enabled',
            fontFamily: 'style.labelBlock.fontFamily',
            fontSize: 'style.labelBlock.fontSize',
            fontStyle: 'style.labelBlock.fontStyle',
            fontWeight: 'style.labelBlock.fontWeight',
            horizontalAlign: 'content.title.align',
            icon: 'content.title.icon',
            iconColor: 'content.title.iconColor',
            iconPosition: 'content.title.iconPosition',
            iconSize: 'style.labelBlock.iconSize',
            lineHeight: 'style.labelBlock.lineHeight',
            position: 'content.title.position',
            width: 'content.title.width',
          },
          {
            excludedPositions: ['right'],
          },
        ),
      ],
      true,
    ),
  ]);
}
